/* eslint-disable react/require-default-props, react/display-name */
import { Badge, Box, Flex } from '@chakra-ui/core';
import React from 'react';
import { EventProps } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import { ReactComponent as _IconEditAvailability } from '../../../assets/img/edit-box-fill.svg';
import { AppointmentAvailability } from '../../../firebase/firestore/documents/appointmentAvaiability';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import { CalendarEventType } from '../CalendarEventType';
import { CalendarAvailabilityType } from '../CalendarAvailabilityType';
import { CalendarScheduleTypeEnum } from '../CalendarScheduleTypeEnum';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from '../CalendarSidebarTab';
import { useGetMeetingCount } from './hooks/useGetMeetingCount';

const IconEditAvailability = React.memo(_IconEditAvailability);

interface CalendarEventComponentProps {
  event: EventProps<CalendarEventType>['event'];
  activeTab?: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum;
  checkedPositions?: string[];
}

interface AvailabilityBadgeProps {
  count: number;
  isAdhoc: boolean;
  absolute?: boolean;
}

const AvailabilityBadge = React.memo(({ count, isAdhoc, absolute = false }: AvailabilityBadgeProps) => (
  <Badge
    {...(absolute ? { position: 'absolute', top: '4px', right: '5px' } : {})}
    colorScheme={isAdhoc ? /* istanbul ignore next */ 'purple' : 'blue'}
    variant="solid"
    fontSize="sm"
    textAlign="center"
    lineHeight="20px"
    borderRadius="50%"
    width="20px"
    height="20px"
  >
    {count}
  </Badge>
));

export const DayEvent = React.memo(({ event, activeTab, checkedPositions }: CalendarEventComponentProps) => {
  const { t } = useTranslation('calendar');

  const isAvailabilityEvent = event.resource.type === CalendarScheduleTypeEnum.AVAILABILITY;
  const availability = isAvailabilityEvent ? (event.resource as CalendarAvailabilityType).availability : undefined;

  const getMeetingCount = useGetMeetingCount({ checkedPositionsIds: checkedPositions, activeTab });
  const meetingCount = getMeetingCount(availability);

  return (
    <span data-testid="DayCell">
      {event.title === 'Availability' ? t('calendar.availability') : event.title}
      {event.bookingCapacity ? ` | ${event.bookingCapacity}` : ''}
      <br />
      {event.title === 'Availability' && meetingCount > 0 && availability && (
        <AvailabilityBadge count={meetingCount} isAdhoc={availability.adhoc ?? /* istanbul ignore next */ false} absolute />
      )}
    </span>
  );
});

export const WeekEvent = React.memo(({ event, activeTab, checkedPositions }: CalendarEventComponentProps) => {
  const { t } = useTranslation('calendar');

  const isAvailabilityEvent = event.resource.type === CalendarScheduleTypeEnum.AVAILABILITY;
  const availability = isAvailabilityEvent ? (event.resource as CalendarAvailabilityType).availability : undefined;

  const getMeetingCount = useGetMeetingCount({ checkedPositionsIds: checkedPositions, activeTab });
  const meetingCount = getMeetingCount(availability);

  return (
    <span data-testid="WeekCell">
      {isAvailabilityEvent ? t('calendar.availability') : event.title}
      {event.bookingCapacity ? ` | ${event.bookingCapacity}` : ''}
      <br />
      {isAvailabilityEvent && meetingCount > 0 && availability && (
        <AvailabilityBadge count={meetingCount} isAdhoc={availability.adhoc ?? /* istanbul ignore next */ false} absolute />
      )}
    </span>
  );
});

interface MonthEventProps extends CalendarEventComponentProps {
  administrationCalendar: boolean;
  openAppointmentDrawerUpdateBooking: (availability: AppointmentAvailability) => void;
}

export const MonthEvent = React.memo(
  ({ event, activeTab, checkedPositions, administrationCalendar, openAppointmentDrawerUpdateBooking }: MonthEventProps) => {
    const { t } = useTranslation('calendar');
    const { currentUserProfile } = useCurrentUserProfile();

    const isAvailabilityEvent = event.resource.type === CalendarScheduleTypeEnum.AVAILABILITY;
    const availability = isAvailabilityEvent ? (event.resource as CalendarAvailabilityType).availability : undefined;

    const getMeetingCount = useGetMeetingCount({ checkedPositionsIds: checkedPositions, activeTab });
    const meetingCount = getMeetingCount(availability);

    if (isAvailabilityEvent && availability) {
      const { bookedTime, availabilityTime } = event.resource as CalendarAvailabilityType;
      return (
        <Flex className="calendar-event-cell" data-testid="MonthCell">
          <Box width="calc(100% - 25px)" mr={2}>
            <span className="booked-time">{`${t('calendar.totalBooked')} - ${bookedTime} ${t('calendar.hrs')}`}</span>
            <span className="available-time">{`${t('calendar.availability')} - ${availabilityTime} ${t('calendar.hrs')}`}</span>
          </Box>
          <Box>
            {event.title === 'Availability' && meetingCount > 0 && (
              <AvailabilityBadge count={meetingCount} isAdhoc={availability.adhoc ?? /* istanbul ignore next */ false} />
            )}
            <span className="meeting-number">
              {!administrationCalendar && availability.userId === currentUserProfile?.id && (
                <Box
                  cursor="pointer"
                  className="action-btns"
                  onClick={() => openAppointmentDrawerUpdateBooking(availability)}
                  data-testid="EditScheduleBtn"
                  position="absolute"
                  right="5px"
                  bottom="10px"
                >
                  <IconEditAvailability width={15} height={15} />
                </Box>
              )}
            </span>
          </Box>
        </Flex>
      );
    }

    if (event.resource.type === CalendarScheduleTypeEnum.GROUP_EVENT) {
      return (
        <Box className="calendar-event-cell">
          <span className="meeting-number">View Group Event</span>
        </Box>
      );
    }

    if (
      event.resource.type === CalendarScheduleTypeEnum.GOOGLE_EVENT ||
      event.resource.type === CalendarScheduleTypeEnum.OUTLOOK_EVENT
    ) {
      return (
        <Box className="calendar-event-cell">
          <span className="event title">{event.title}</span>
        </Box>
      );
    }

    return <span />;
  },
);
